import { Node, mergeAttributes } from "@tiptap/core";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    footnote: {
      addFootnote: (options: { content: string }) => ReturnType;
    };
  }
}

/**
 * Counts all existing footnote nodes in the document to determine
 * the next footnote number.
 */
function countFootnotes(doc: ProseMirrorNode): number {
  let count = 0;
  doc.descendants((node) => {
    if (node.type.name === "footnote") count++;
  });
  return count;
}

export const Footnote = Node.create({
  name: "footnote",

  group: "inline",

  inline: true,

  atom: true,

  addAttributes() {
    return {
      content: {
        default: "",
        parseHTML: (element) => element.getAttribute("title") ?? "",
      },
      number: {
        default: 1,
        parseHTML: (element) => {
          const text = element.textContent;
          return text ? parseInt(text, 10) || 1 : 1;
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'sup[data-footnote="true"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "sup",
      mergeAttributes(HTMLAttributes, {
        "data-footnote": "true",
        class: "footnote",
        title: HTMLAttributes.content as string,
      }),
      String(HTMLAttributes.number),
    ];
  },

  addCommands() {
    return {
      addFootnote:
        ({ content }) =>
        ({ tr, dispatch, state }) => {
          if (dispatch) {
            const number = countFootnotes(state.doc) + 1;
            const node = this.type.create({ content, number });
            tr.replaceSelectionWith(node);
          }
          return true;
        },
    };
  },
});
